import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { map, catchError, switchMap, withLatestFrom, filter, take, tap } from 'rxjs/operators';
import { UserService } from '../../../core/services/user.service';
import { SharedDomainService } from '../../../core/services/shared-domain.service';
import { transformUserResponse } from '../../utils/user-transformers';
import * as UserActions from './user.actions';
import * as AppInitActions from '../../app-init/app-init.actions';
import * as DomainsActions from '../domains/domains.actions';
import * as ProfileActions from '../profile/profile.actions';
import { selectAllDomains, selectCurrentDomainId } from '../domains/domains.selectors';
import { selectDefaultDomainUuid } from '../profile/profile.selectors';
import { Domain } from '../models/domain.model';

@Injectable()
export class UserEffects {
  private actions$ = inject(Actions);
  private store = inject(Store);
  private userService = inject(UserService);
  private sharedDomainService = inject(SharedDomainService);

  loadUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.loadUser),
      switchMap(() =>
        this.userService.getMe().pipe(
          map((response) => {
            const user = transformUserResponse(response.data);

            return UserActions.loadUserSuccess({
              user,
            });
          }),
          catchError((error: unknown) => {
            const errorMessage =
              error instanceof Error
                ? error.message
                : (error as { error?: { message?: string } })?.error?.message ||
                  (error as { message?: string })?.message ||
                  'Failed to load user data';

            return of(
              UserActions.loadUserFailure({
                error: errorMessage,
              })
            );
          })
        )
      )
    )
  );

  markUserInitialized$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.loadUserSuccess),
      map(() => AppInitActions.markRequestComplete({ requestKey: 'user' }))
    )
  );

  markUserFailed$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.loadUserFailure),
      map(() => AppInitActions.markRequestFailed({ requestKey: 'user' }))
    )
  );

  setDefaultDomain$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProfileActions.loadProfileSuccess),
      withLatestFrom(
        this.store.select(selectAllDomains),
        this.store.select(selectDefaultDomainUuid),
        this.store.select(selectCurrentDomainId)
      ),
      filter(([, domains, , currentDomainId]) => {
        return domains.length > 0 && !currentDomainId;
      }),
      map(([, domains, defaultDomainUuid]) => {
        const domainId = this.resolveInitialDomainId(domains, defaultDomainUuid);

        return DomainsActions.setCurrentDomainId({ domainId });
      })
    )
  );

  setDefaultDomainAfterDomainsLoad$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DomainsActions.loadDomainsSuccess),
      withLatestFrom(
        this.store.select(selectDefaultDomainUuid),
        this.store.select(selectCurrentDomainId)
      ),
      filter(([action, , currentDomainId]) => {
        return action.domains.length > 0 && !currentDomainId;
      }),
      map(([action, defaultDomainUuid]) => {
        const domainId = this.resolveInitialDomainId(action.domains, defaultDomainUuid);

        return DomainsActions.setCurrentDomainId({ domainId });
      })
    )
  );

  /**
   * Cleans up the shared domain (URL param, localStorage) once the initial
   * domain has been selected. Uses `take(1)` so it only runs for the very
   * first `setCurrentDomainId` dispatch during app initialisation.
   */
  cleanupSharedDomain$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DomainsActions.setCurrentDomainId),
        take(1),
        tap(() => this.sharedDomainService.clearSharedDomain())
      ),
    { dispatch: false }
  );

  /**
   * Determines the initial domain to select with the following priority:
   * 1. Shared domain (from URL `?domain=` param or localStorage `sharedDomainUuid`)
   * 2. User's default domain (from profile settings)
   * 3. First available domain in the list
   *
   * NOTE: Does NOT clear the shared domain – the cleanup is handled by the
   * `cleanupSharedDomain$` effect so that both `setDefaultDomain$` and
   * `setDefaultDomainAfterDomainsLoad$` always see the same cached value.
   */
  private resolveInitialDomainId(
    domains: readonly Domain[],
    defaultDomainUuid: string | null
  ): string {
    // Priority 1: Shared domain from URL or localStorage
    const sharedDomainUuid = this.sharedDomainService.getSharedDomainUuid();

    if (sharedDomainUuid) {
      const sharedDomainExists = domains.some((domain) => domain.uuid === sharedDomainUuid);

      if (sharedDomainExists) {
        return sharedDomainUuid;
      }

      console.warn('[SharedDomain] User does not have access to shared domain:', sharedDomainUuid);
      this.sharedDomainService.markDomainSwitchRejected();
    }

    // Priority 2: Default domain from profile
    if (defaultDomainUuid) {
      const defaultExists = domains.some((domain) => domain.uuid === defaultDomainUuid);

      if (defaultExists) {
        return defaultDomainUuid;
      }
    }

    // Priority 3: First available domain
    return domains[0].uuid;
  }
}
